/**
 * Utilitários para retry e fallback de chamadas à API
 */

export interface RetryOptions {
  maxRetries?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  shouldRetry?: (error: Error, attempt: number) => boolean;
}

/**
 * Executa uma função com retry automático
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    delayMs = 1000,
    backoffMultiplier = 2,
    shouldRetry = (error, attempt) => attempt < maxRetries
  } = options;

  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (!shouldRetry(lastError, attempt)) {
        break;
      }
      
      if (attempt < maxRetries) {
        const delay = delayMs * Math.pow(backoffMultiplier, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
}

/**
 * Tenta múltiplas URLs de API até encontrar uma que funcione
 */
export async function tryMultipleEndpoints<T>(
  endpoints: string[],
  requestFn: (url: string) => Promise<T>
): Promise<T> {
  let lastError: Error;
  
  for (const endpoint of endpoints) {
    try {
      return await requestFn(endpoint);
    } catch (error) {
      lastError = error as Error;
      
      // If this is not a retryable error (business logic error), 
      // throw it immediately without trying other endpoints
      if (!isRetryableError(lastError)) {
        throw lastError;
      }
    }
  }
  
  throw new Error(`Todos os endpoints falharam. Último erro: ${lastError?.message}`);
}

/**
 * Verifica se um erro é temporário e pode ser retentado
 */
export function isRetryableError(error: Error): boolean {
  const message = error.message.toLowerCase();
  
  // Erros de rede que podem ser temporários
  if (message.includes('fetch') || 
      message.includes('network') || 
      message.includes('timeout') ||
      message.includes('connection')) {
    return true;
  }
  
  // Erros HTTP 5xx são temporários
  if (message.includes('500') || 
      message.includes('502') || 
      message.includes('503') || 
      message.includes('504')) {
    return true;
  }
  
  return false;
}

/**
 * Cria uma função de retry personalizada para APIs
 */
export function createApiRetry(options: RetryOptions = {}) {
  return <T>(fn: () => Promise<T>): Promise<T> => {
    return withRetry(fn, {
      maxRetries: 2,
      delayMs: 1000,
      shouldRetry: (error, attempt) => {
        return attempt < (options.maxRetries || 2) && isRetryableError(error);
      },
      ...options
    });
  };
}